import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../components/ui/dialog";
import { useSession } from "../context/SessionContext";
import { SquareUserRound } from "lucide-react";
import { Button } from "../components/ui/button";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import {
  format,
  isToday,
  isYesterday,
  differenceInDays,
  isValid,
} from "date-fns";
import { Phone, IdCard, User, Shield, Copy, FileUser, Edit } from "lucide-react";
import { Tooltip } from "./ui/tooltip";
import { TooltipProvider, TooltipTrigger, TooltipContent } from "./ui/tooltip";
import toast from "react-hot-toast";
import { Input } from "./ui/input";

const UserDialogHeader = ({ data, user }: any) => {
  const { t } = useTranslation();
  
  const hasError = data?.error || data?.message;
  const [hovered, setHovered] = useState(false);
  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    toast.success(t("copied_to_clipboard"));
  };

  if (hasError) {
    return (
      <div className="flex flex-col gap-3">
        <div className="text-lg font-semibold text-[#13161A] mb-3">
          {t("unregistered_user")}
        </div>

        {user?.phone && (
          <div className="flex items-center gap-2 bg-[#F6F7F9] px-3 py-2 rounded text-sm text-[#252A32] mb-3">
            <Phone size={16} color={"#105BDB"} />
            <span>{user.phone}</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                  asChild
                >
                  <button
                    onClick={() => copyToClipboard(user.phone)}
                    className="ml-auto text-gray-400 hover:text-black cursor-pointer"
                  >
                    <Copy size={14} color={"#252A32"} />
                  </button>
                </TooltipTrigger>

                {hovered && <TooltipContent>{t("copy_phone")}</TooltipContent>}
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
        
        <div className="flex items-center gap-3 bg-[#FEF3CD] px-4 py-3 rounded-lg border border-[#FDE68A]">
          <div className="flex-shrink-0">
            <User size={20} color={"#D97706"} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#92400E]">
              {t("registration_not_completed")}
            </p>
            <p className="text-xs text-[#A16207] mt-1">
              {t("personal_data_unavailable")}
            </p>
          </div>
        </div>

        <div className="bg-[#F8F9FA] px-4 py-3 rounded-lg border border-[#E9ECEF]">
          <h4 className="text-sm font-medium text-[#495057] mb-2">
            {t("available_information")}
          </h4>
          <p className="text-xs text-[#6C757D]">
            {t("conversation_history_only")}<br/>
            {t("personal_data_after_registration")}<br/>
            {t("can_complete_anytime")}
          </p>
        </div>
      </div>
    );
  }

  const personal = data?.patient?.personalData;
  const fullName = personal?.fullName || t("unknown_name");
  const phone = personal?.phones?.[0]?.value || t("no_phone");
  const dni = personal?.identifications?.[0]?.value || t("no_document");
  const insurance = data?.guarantors?.[0]?.name || t("no_insurance");
  const chn = personal?.chn ? `CHN ${personal.chn}` : t("unknown_chn");

  const gender = personal?.gender?.name || t("unknown_gender");
  const age = personal?.birthDate
    ? Math.floor(
        (Date.now() - personal.birthDate) / (1000 * 60 * 60 * 24 * 365.25)
      )
    : null;

  return (
    <div className="flex flex-col gap-3">
      <div className="text-lg font-semibold text-[#13161A] mb-3">
        {fullName}
      </div>

      <div className="flex items-center gap-2 bg-[#F6F7F9] px-3 py-2 rounded text-sm text-[#252A32]">
        <Phone size={16} color={"#105BDB"} />
        <span>{phone}</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              asChild
            >
              <button
                onClick={() => copyToClipboard(phone)}
                className="ml-auto text-gray-400 hover:text-black cursor-pointer"
              >
                <Copy size={14} color={"#252A32"} />
              </button>
            </TooltipTrigger>

            {hovered && <TooltipContent>{t("copy_phone")}</TooltipContent>}
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex items-center gap-2 bg-[#F6F7F9] px-3 py-2 rounded text-sm text-[#252A32]">
        <IdCard size={16} color={"#105BDB"} />
        <span>{dni}</span>
      </div>
      <div className="flex items-center gap-2 bg-[#F6F7F9] px-3 py-2 rounded text-sm text-[#252A32]">
        <FileUser size={16} color={"#105BDB"} />
        <span>{chn}</span>
      </div>

      <div className="flex items-center gap-2 bg-[#F6F7F9] px-3 py-2 rounded text-sm text-[#252A32]">
        <User size={16} color={"#105BDB"} />
        <span>
          {gender}, {age ? `${age} años` : t("unknown_age")}
        </span>
      </div>

      <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded text-sm text-[#252A32]">
        <Shield size={16} color={"#28A745"} />
        <span>{insurance}</span>
      </div>
    </div>
  );
};

const EditExternalIdView = ({ user, onSave, onCancel, t }: any) => {
  const [externalId, setExternalId] = useState(user?.external_id || "");
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!externalId.trim()) {
      toast.error(t("external_id_required") || "El external_id es requerido");
      return;
    }

    setIsSaving(true);
    await onSave(externalId);
    setIsSaving(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="text-lg font-semibold text-[#13161A]">
        {t("edit_external_id") || "Editar External ID"}
      </div>

      <div className="bg-[#F8F9FA] px-4 py-3 rounded-lg border border-[#E9ECEF]">
        <p className="text-xs text-[#6C757D]">
          {t("external_id_description") || "El external_id es el identificador único del paciente en el sistema hospitalario. Asegúrate de introducir el ID correcto."}
        </p>
      </div>

      {user?.phone && (
        <div className="flex items-center gap-2 bg-[#F6F7F9] px-3 py-2 rounded text-sm text-[#252A32]">
          <Phone size={16} color={"#105BDB"} />
          <span>{user.phone}</span>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-[#252A32]">
          {t("external_id") || "External ID"} <span className="text-red-500">*</span>
        </label>
        <Input
          type="text"
          value={externalId}
          onChange={(e) => setExternalId(e.target.value)}
          placeholder={t("enter_external_id") || "Introduce el external_id"}
          className="w-full border rounded-md"
          disabled={isSaving}
        />
      </div>

      <div className="flex justify-end gap-3">
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={isSaving}
          className="bg-white border"
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving || !externalId.trim()}
          className="bg-[#105BDB] hover:bg-[#679BF4] active:bg-[#0A3785]"
        >
          {isSaving ? t("saving") || "Guardando..." : t("save")}
        </Button>
      </div>
    </div>
  );
};

export default function UserHeader({
  user,
  isHistoryPage,
  onHistoryClick,
  conversationCounts,
  lastMessageDate,
}: {
  user: any;
  isHistoryPage: boolean;
  onHistoryClick: (phone: string) => void;
  conversationCounts: { [phone: string]: number };
  lastMessageDate?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [patientData, setPatientData] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const { session, getFullPatientDataByExternalId } = useSession();

  const getId = async () => {
    if (!user?.external_id) {
      setPatientData({ error: "No external_id" });
      return;
    }
    const data = await getFullPatientDataByExternalId(user.external_id);
    setPatientData(data);
  };

  useEffect(() => {
    if (open) {
      getId();
    }
  }, [user?.external_id, open]);

  const handleSaveExternalId = async (newExternalId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/updateExternalId/${user.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `${session!.token_type} ${session!.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ external_id: newExternalId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update external_id");
      }

      toast.success(t("external_id_updated") || "External ID actualizado correctamente");

      user.external_id = newExternalId;

      const data = await getFullPatientDataByExternalId(newExternalId);
      setPatientData(data);

      setIsEditMode(false);
    } catch (error) {
      console.error("Error updating external_id:", error);
      toast.error(t("error_updating_external_id") || "Error al actualizar el external_id");
    }
  };

  const renderDateLabel = () => {
    if (!lastMessageDate || !isValid(new Date(lastMessageDate))) return null;
    const date = new Date(lastMessageDate);

    if (isToday(date)) {
      return t("date_label.today", { time: format(date, "HH:mm") });
    }

    if (isYesterday(date)) {
      return t("date_label.yesterday", { time: format(date, "HH:mm") });
    }

    const days = differenceInDays(new Date(), date);
    return t(
      days === 1 ? "date_label.days_ago" : "date_label.days_ago_plural",
      { count: days }
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setIsEditMode(false);
        }
      }}>
        <div className="flex items-center justify-between w-full">
          <div
            className="flex items-center gap-2 text-[#105BDB] cursor-pointer hover:text-[#679BF4] active:text-[#0A3785]"
            onClick={() => {
              setOpen(true);
            }}
          >
            <SquareUserRound size={16} />
            <h2 className="text-sm font-bold">{user.name || user.displayName}</h2>
          </div>
          {renderDateLabel() && (
            <div className="flex">
              {renderDateLabel()}
            </div>
          )}
        </div>

        <DialogContent className="bg-white w-[393px]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between"></DialogTitle>
            {isEditMode ? (
              <EditExternalIdView
                user={user}
                onSave={handleSaveExternalId}
                onCancel={() => setIsEditMode(false)}
                t={t}
              />
            ) : (
              <>
                {patientData ? (
                  <UserDialogHeader 
                    data={patientData} 
                    user={user}
                    onEditClick={() => setIsEditMode(true)}
                  />
                ) : (
                  <div className="text-sm text-gray-500">
                    {t("loading_patient_data") || "Cargando datos del paciente..."}
                  </div>
                )}
              </>
            )}
          </DialogHeader>
          {!isEditMode && (
            <DialogFooter className="gap-1 justify-end">
              <Button
                onClick={() => setIsEditMode(true)}
                variant="outline"
                className="bg-white border border-[#E2E4E9] text-[#252A32] hover:bg-[#F6F7F9]"
              >
                <Edit size={16} className="mr-2" />
                {t("edit") || "Modificar"}
              </Button>
              <DialogClose asChild>
                <Button variant="secondary" className="bg-white border">
                  {t("cancel")}
                </Button>
              </DialogClose>
              {!isHistoryPage && (
                <DialogClose asChild>
                  <Button
                    onClick={() => onHistoryClick(user.phone)}
                    className="bg-[#105BDB] hover:bg-[#679BF4] active:bg-[#0A3785] px-4 py-2 text-base font-normal"
                  >
                    {t("view_chat_history")}
                  </Button>
                </DialogClose>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
